import { useMemo } from "react";
import { Redirect, useLocation, useParams } from "react-router-dom";
import { IconType, GenIcon } from "react-icons/lib/esm/iconBase";

import { findCarBySlug } from "@mywheels/common/lib/cars/utils";
import { useGraphCMS } from "@mywheels/common/lib/useGraphCMS";
import { useTranslation } from "@mywheels/common/lib/translation";

import { ToolbarSpacer } from "web/layout/Toolbar";
import { useRoutes } from "web/lib/i18n";
import { Spinner, useScrollToTop } from "web/ui";

import { NotFoundPage } from "../NotFoundPage";
import { ManualSectionLink } from "./ManualSectionLink";
import { ManualPageHeader } from "./ManualPageHeader";
import { GetWebHandleidingCarQuery } from "./queries";
import { Car } from "@mywheels/common/lib/cars/types";

export default function CarManualPageWrapper() {
  // eslint-disable-next-line
  const { carId } = useParams<any>();
  const routes = useRoutes();

  const car = findCarBySlug(carId);

  if (!car) {
    return <NotFoundPage />;
  } else if (car.id !== carId) {
    return <Redirect to={routes.CarManual.url({ carId: car.id })} />;
  } else {
    return <CarManualPage car={car} />;
  }
}

function CarManualPage({ car }: { car: Car }) {
  useScrollToTop();
  const { language } = useTranslation();

  const find = useGraphCMS(GetWebHandleidingCarQuery, {
    slug: car.id,
    locale: language,
  });

  if (!find.isLoading && !find.data) {
    return <NotFoundPage />;
  }

  const sectionsToDisplay = find.data?.manualSections;

  return (
    <div>
      <ToolbarSpacer />

      <ManualPageHeader car={car} graphcmsCar={find.data} />

      <div className="container my-6 md:my-10">
        <div className="row">
          <div className="md:col-1" />
          <div className="col-12 md:col-10">
            {!sectionsToDisplay ? (
              <Spinner />
            ) : (
              <div className="row">
                {[
                  [0, Math.ceil(sectionsToDisplay.length / 2)],
                  [
                    Math.ceil(sectionsToDisplay.length / 2),
                    sectionsToDisplay.length,
                  ],
                ].map(([a, b], j) => {
                  return (
                    <div className="col-12 md:col-6" key={j}>
                      {sectionsToDisplay.slice(a, b).map((section) => {
                        return (
                          <SectionLink
                            carId={car.id}
                            section={section}
                            key={section.id}
                          />
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionLink({
  carId,
  section,
}: {
  carId: string;
  section: {
    id: string;
    name: string;
    slug: string;
    icon?: {
      name: string;
      // eslint-disable-next-line
      data: any;
    };
  };
}) {
  const location = useLocation();

  const Icon = useMemo<IconType | undefined>(() => {
    if (section.icon) {
      // eslint-disable-next-line
      return GenIcon(section.icon.data as any);
    }
  }, [section.icon]);

  return (
    <ManualSectionLink
      search={location.search}
      carId={carId}
      sectionId={section.slug}
      Icon={Icon}
    >
      {section.name}
    </ManualSectionLink>
  );
}
